import React from 'react';
import { Link } from 'react-router-dom';
import { Cloud, Brain, Network, Database, Lock, HardDrive, RefreshCw, ArrowRight, CheckCircle } from 'lucide-react';

export default function Soluciones() {
  const soluciones = [
    {
      icon: Cloud,
      titulo: 'Nube Híbrida + IA Agéntica',
      descripcion: 'Infraestructura como servicio con optimización inteligente',
      detalles: 'HPE GreenLake con inteligencia artificial para gestión autónoma y optimización continua de recursos cloud.',
      tecnologias: ['HPE GreenLake', 'GreenLake Intelligence', 'Private Cloud', 'Edge-to-Cloud'],
      path: '/soluciones/nube-hibrida',
      color: 'blue'
    },
    {
      icon: Brain,
      titulo: 'Inteligencia Artificial',
      descripcion: 'IA empresarial con seguridad y gobernanza',
      detalles: 'Implementa LLMs, machine learning y modelos de lenguaje con infraestructura dedicada y control total.',
      tecnologias: ['HPE AI Services', 'Private Cloud AI', 'NVIDIA GPUs', 'MLOps'],
      path: '/soluciones/inteligencia-artificial',
      color: 'purple'
    },
    {
      icon: Network,
      titulo: 'Conectividad Empresarial',
      descripcion: 'Redes inteligentes con IA integrada',
      detalles: 'HPE Aruba Networking con Mist AI para optimización automática, seguridad perimetral y SASE.',
      tecnologias: ['Aruba Central', 'Mist AI', 'Juniper SRX', 'SASE', 'Wi-Fi 6E/7'],
      path: '/soluciones/conectividad',
      color: 'green'
    },
    {
      icon: Database,
      titulo: 'Nube y CloudOps',
      descripcion: 'Gestión multi-cloud con AIOps',
      detalles: 'Orquestación, monitoreo AIOps y modernización de aplicaciones para ambientes híbridos.',
      tecnologias: ['HPE OpsRamp', 'Morpheus', 'App Modernization', 'Kubernetes'],
      path: '/soluciones/nube',
      color: 'indigo'
    },
    {
      icon: Lock,
      titulo: 'Seguridad Empresarial',
      descripcion: 'Protección integral desde el firmware',
      detalles: 'Arquitectura Zero Trust con HPE GreenLake, cumplimiento automatizado y SOC 24/7.',
      tecnologias: ['Zero Trust', 'SIEM', 'EDR', 'Compliance', 'SOC'],
      path: '/soluciones/seguridad',
      color: 'red'
    },
    {
      icon: HardDrive,
      titulo: 'Gestión de Datos',
      descripcion: 'Cómputo, almacenamiento y protección',
      detalles: 'Infraestructura completa: servidores ProLiant, storage Alletra, protección Zerto y data services.',
      tecnologias: ['HPE ProLiant', 'Alletra Storage', 'Zerto', 'Data Fabric'],
      path: '/soluciones/datos',
      color: 'teal'
    },
    {
      icon: RefreshCw,
      titulo: 'Servicios Gestionados',
      descripcion: 'Operación 24/7 de tu infraestructura',
      detalles: 'Managed Services para infraestructura, cloud, seguridad, bases de datos y aplicaciones.',
      tecnologias: ['NOC 24/7', 'CloudOps', 'SecOps', 'DBA', 'Service Desk'],
      path: '/soluciones/servicios-gestionados',
      color: 'orange'
    }
  ];

  const colorClasses = {
    blue: 'from-blue-600 to-blue-700',
    purple: 'from-purple-600 to-purple-700',
    green: 'from-green-600 to-green-700',
    indigo: 'from-indigo-600 to-indigo-700',
    red: 'from-red-600 to-red-700',
    teal: 'from-teal-600 to-teal-700',
    orange: 'from-orange-600 to-orange-700'
  };

  const iconBgClasses = {
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    green: 'bg-green-50 text-green-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    red: 'bg-red-50 text-red-600',
    teal: 'bg-teal-50 text-teal-600',
    orange: 'bg-orange-50 text-orange-600'
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section 
        className="relative py-12 md:py-24 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=2134)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-allti-blue/90 via-blue-900/85 to-allti-blue/90"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-hpe-green/20 backdrop-blur-sm px-4 md:px-5 py-2 md:py-2.5 rounded-full mb-6 md:mb-8 border border-hpe-green/40">
              <div className="w-2 h-2 bg-hpe-green rounded-full animate-pulse"></div>
              <span className="text-white font-semibold text-xs md:text-sm tracking-wide">Portafolio Completo HPE</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
              Soluciones Tecnológicas
              <br />
              <span className="bg-gradient-to-r from-allti-blue via-hpe-green to-allti-blue bg-clip-text text-transparent">
                para tu Empresa
              </span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl max-w-3xl mx-auto mb-8 md:mb-10 text-gray-200">
              Portafolio completo de soluciones HPE: desde infraestructura core hasta IA, 
              conectividad inteligente y servicios gestionados
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 md:gap-6 max-w-2xl mx-auto mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 md:p-4 border border-white/20">
                <p className="text-2xl md:text-3xl font-bold text-allti-blue mb-1">7</p>
                <p className="text-xs md:text-sm text-gray-200">Soluciones</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 md:p-4 border border-white/20">
                <p className="text-2xl md:text-3xl font-bold text-hpe-green mb-1">100%</p>
                <p className="text-xs md:text-sm text-gray-200">HPE Certified</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 md:p-4 border border-white/20">
                <p className="text-2xl md:text-3xl font-bold text-white mb-1">24/7</p>
                <p className="text-xs md:text-sm text-gray-200">Soporte</p>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="#soluciones" 
                className="bg-allti-red text-white px-6 md:px-8 py-3 md:py-4 rounded-xl hover:bg-red-700 transition-all text-sm md:text-base font-semibold shadow-xl shadow-allti-red/30"
              >
                Explorar Soluciones
              </a>
              <Link 
                to="/contacto" 
                className="bg-white/10 backdrop-blur-sm text-white px-6 md:px-8 py-3 md:py-4 rounded-xl hover:bg-white/20 transition-all text-sm md:text-base font-semibold border border-white/30"
              >
                Solicitar Consultoría
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introducción */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg md:text-xl text-gray-600 mb-6">
            Como <strong className="text-allti-blue">partner certificado de HPE</strong>, ALLTI ofrece soluciones integrales 
            que abarcan desde infraestructura de cómputo y almacenamiento hasta inteligencia artificial, 
            redes empresariales, seguridad y servicios gestionados.
          </p>
          <p className="text-base md:text-lg text-gray-600">
            Cada solución está diseñada para empresas que buscan modernizar su infraestructura TI 
            con tecnología de clase mundial.
          </p>
        </div>
      </section>

      {/* Grid de Soluciones */}
      <section id="soluciones" className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {soluciones.map((solucion, index) => {
              const Icon = solucion.icon;
              return (
                <Link
                  key={index}
                  to={solucion.path}
                  className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200"
                >
                  <div className={`h-2 bg-gradient-to-r ${colorClasses[solucion.color]}`}></div>
                  <div className="p-8">
                    <div className={`${iconBgClasses[solucion.color]} w-16 h-16 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                      <Icon size={32} />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-allti-blue transition-colors">
                      {solucion.titulo}
                    </h3>
                    
                    <p className="text-lg text-allti-blue font-semibold mb-3">
                      {solucion.descripcion}
                    </p>
                    
                    <p className="text-gray-600 mb-4">
                      {solucion.detalles}
                    </p>
                    
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Tecnologías incluidas:</p>
                      <div className="flex flex-wrap gap-2">
                        {solucion.tecnologias.map((tech, idx) => (
                          <span 
                            key={idx}
                            className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center text-allti-blue font-semibold group-hover:underline">
                      Ver solución completa <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Beneficios Generales */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">¿Por qué elegir ALLTI y HPE?</h2>
            <p className="text-xl text-gray-600">
              Ventajas de trabajar con un partner certificado
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="bg-allti-blue/10 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <CheckCircle className="text-allti-blue" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Experiencia Comprobada</h3>
              <p className="text-gray-600">
                Más de 15 años implementando soluciones HPE en empresas líderes de México y Latinoamérica.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="bg-hpe-green/10 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <CheckCircle className="text-hpe-green" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Soporte Integral</h3>
              <p className="text-gray-600">
                Desde diseño y consultoría hasta implementación, migración y soporte 24/7 post-implementación.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="bg-allti-red/10 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <CheckCircle className="text-allti-red" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Tecnología de Punta</h3>
              <p className="text-gray-600">
                Acceso a las últimas innovaciones de HPE: IA, edge computing, nube híbrida y automatización.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ecosistema HPE - Detallado */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-hpe-green/20 backdrop-blur-sm px-6 py-2 rounded-full mb-4 border border-hpe-green/40">
              <span className="text-white font-semibold">Partner Certificado HPE</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Ecosistema Tecnológico HPE</h2>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Portafolio completo de marcas líderes del ecosistema HPE que implementamos y soportamos
            </p>
          </div>

          {/* Grid de Logos con Descripciones */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* HPE Core */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/15 transition-all">
              <div className="bg-white rounded-lg p-4 mb-4 flex items-center justify-center h-24">
                <img 
                  src="/images/HPE Full.png" 
                  alt="HPE" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">HPE Infrastructure</h3>
              <p className="text-gray-300 text-sm mb-3">
                Servidores ProLiant, almacenamiento Alletra, soluciones hiperconvergentes y infraestructura core.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-white/20 text-white px-2 py-1 rounded">ProLiant</span>
                <span className="text-xs bg-white/20 text-white px-2 py-1 rounded">Alletra</span>
                <span className="text-xs bg-white/20 text-white px-2 py-1 rounded">SimpliVity</span>
              </div>
            </div>

            {/* GreenLake */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/15 transition-all">
              <div className="bg-white rounded-lg p-4 mb-4 flex items-center justify-center h-24">
                <img 
                  src="/images/Greenlake.png" 
                  alt="HPE GreenLake" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">HPE GreenLake</h3>
              <p className="text-gray-300 text-sm mb-3">
                Plataforma de nube híbrida como servicio con IA agéntica, edge-to-cloud y pago por uso.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-hpe-green/30 text-white px-2 py-1 rounded">Cloud Platform</span>
                <span className="text-xs bg-hpe-green/30 text-white px-2 py-1 rounded">AI Intelligence</span>
                <span className="text-xs bg-hpe-green/30 text-white px-2 py-1 rounded">Pay-per-use</span>
              </div>
            </div>

            {/* Aruba */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/15 transition-all">
              <div className="bg-white rounded-lg p-4 mb-4 flex items-center justify-center h-24">
                <img 
                  src="/images/Aruba.png" 
                  alt="Aruba Networks" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Aruba Networking</h3>
              <p className="text-gray-300 text-sm mb-3">
                Redes empresariales con IA integrada: switches, access points, Mist AI y Aruba Central.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-blue-500/30 text-white px-2 py-1 rounded">Wi-Fi 6E/7</span>
                <span className="text-xs bg-blue-500/30 text-white px-2 py-1 rounded">Mist AI</span>
                <span className="text-xs bg-blue-500/30 text-white px-2 py-1 rounded">SASE</span>
              </div>
            </div>

            {/* Juniper */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/15 transition-all">
              <div className="bg-white rounded-lg p-4 mb-4 flex items-center justify-center h-24">
                <img 
                  src="/images/Juniper.png" 
                  alt="Juniper Networks" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Juniper Networks</h3>
              <p className="text-gray-300 text-sm mb-3">
                Firewalls de próxima generación SRX, switches empresariales y soluciones SD-WAN.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-yellow-500/30 text-white px-2 py-1 rounded">SRX Firewall</span>
                <span className="text-xs bg-yellow-500/30 text-white px-2 py-1 rounded">SD-WAN</span>
                <span className="text-xs bg-yellow-500/30 text-white px-2 py-1 rounded">Security</span>
              </div>
            </div>

            {/* Zerto */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/15 transition-all">
              <div className="bg-white rounded-lg p-4 mb-4 flex items-center justify-center h-24">
                <img 
                  src="/images/Zerto.png" 
                  alt="Zerto" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Zerto</h3>
              <p className="text-gray-300 text-sm mb-3">
                Continuidad de negocio y recuperación ante desastres con RPO cercano a cero.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-green-500/30 text-white px-2 py-1 rounded">DR</span>
                <span className="text-xs bg-green-500/30 text-white px-2 py-1 rounded">Backup</span>
                <span className="text-xs bg-green-500/30 text-white px-2 py-1 rounded">Replication</span>
              </div>
            </div>

            {/* OpsRamp */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/15 transition-all">
              <div className="bg-white rounded-lg p-4 mb-4 flex items-center justify-center h-24">
                <img 
                  src="/images/Opsramp.png" 
                  alt="OpsRamp" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">HPE OpsRamp</h3>
              <p className="text-gray-300 text-sm mb-3">
                Plataforma AIOps para monitoreo multi-cloud, observabilidad y gestión de eventos.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-purple-500/30 text-white px-2 py-1 rounded">AIOps</span>
                <span className="text-xs bg-purple-500/30 text-white px-2 py-1 rounded">Monitoring</span>
                <span className="text-xs bg-purple-500/30 text-white px-2 py-1 rounded">Multi-Cloud</span>
              </div>
            </div>
          </div>

          {/* Integraciones y Compatibilidad */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">Integración y Compatibilidad Total</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-allti-blue/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-allti-blue" size={32} />
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">Ecosistema Unificado</h4>
                <p className="text-gray-300 text-sm">
                  Todas las soluciones integradas bajo la plataforma HPE GreenLake con gestión centralizada.
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-hpe-green/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-hpe-green" size={32} />
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">Soporte Certificado</h4>
                <p className="text-gray-300 text-sm">
                  Ingenieros certificados en todas las tecnologías del ecosistema HPE y partners.
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-yellow-400/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-yellow-400" size={32} />
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">Escalabilidad Total</h4>
                <p className="text-gray-300 text-sm">
                  Infraestructura que crece con tu negocio, desde edge hasta cloud sin límites.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proceso de Implementación */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nuestro Proceso</h2>
            <p className="text-xl text-gray-600">
              Metodología probada para implementaciones exitosas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-allti-blue text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Análisis y Diseño</h3>
              <p className="text-gray-600">Evaluamos tus necesidades y diseñamos la arquitectura óptima</p>
            </div>

            <div className="text-center">
              <div className="bg-allti-blue text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Propuesta y Cotización</h3>
              <p className="text-gray-600">Presentamos solución técnica con costos transparentes</p>
            </div>

            <div className="text-center">
              <div className="bg-allti-blue text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Implementación</h3>
              <p className="text-gray-600">Desplegamos la solución con mínima interrupción operativa</p>
            </div>

            <div className="text-center">
              <div className="bg-allti-blue text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                4
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Soporte Continuo</h3>
              <p className="text-gray-600">Brindamos soporte 24/7, optimización y mejora continua</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-allti-red to-red-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            ¿Listo para modernizar tu infraestructura?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Agenda una consultoría gratuita con nuestros expertos certificados HPE
          </p>
          <Link
            to="/contacto"
            className="bg-white text-allti-red px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors text-lg font-semibold inline-flex items-center"
          >
            Solicitar Consultoría <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}
